import { useEffect, useState } from "react";

import ContentLayout from "@/layouts/ContentLayout";

import { ValueType } from "@/types/bid";
import { DashboardFilterType } from "@/types/dashboard";

import { DefaultDashboardFilter } from "@/consts/Dashboard";

import { beginDashboardSearch } from "@/libs/dashboardSearch";

import { GetProductRatio, GetStatsByProducts } from "@/services/Dashboard";

import DashboardSingleBarChart from "@/components/DashbaordSingleBarChart";
import DashboardBidSummary from "@/components/DashboardBidSummary";
import DashboardContractTable from "@/components/DashboardContractTable";
import DashboardDemandAgencyBudgetRatio from "@/components/DashboardDemandAgencyBudgetRatio";
import DashboardDemandAgencySupplyEquipmentRatio from "@/components/DashboardDemandAgencySupplyEquipmentRatio";
import DashboardFilter from "@/components/DashboardFilter";
import DashboardHorizontalBarChart from "@/components/DashboardHorizontalBarChart";
import DashboardLineChart from "@/components/DashboardLineChart";
import DashboardPieChart from "@/components/DashboardPieChart";
import DashboardStackedBarChart from "@/components/DashboardStackedBarChart";
import DashboardTab from "@/components/DashboardTab";
import DashboardTopTenList from "@/components/DashboardTopTenList";
import DashboardTTPComparisonChart from "@/components/DashboardTTPComparisonChart";

export default function DashboardBid() {
  const [searchReady, setSearchReady] = useState<boolean>(false);
  // 사용자가 수정 중인 filter
  const [filter, setFilter] = useState<DashboardFilterType>(
    DefaultDashboardFilter
  );
  // 검색을 눌렀을 때 적용되는 filter
  const [appliedFilter, setAppliedFilter] = useState<DashboardFilterType>(
    DefaultDashboardFilter
  );
  // 계약기업 필터 (테이블만 즉시 적용)
  const [bidContractorIds, setBidContractorIds] = useState<ValueType[]>([]);

  const handleSetDefaultFilter = (newFilter: DashboardFilterType) => {
    const result = { ...filter, ...newFilter };
    setFilter(result);
    setAppliedFilter(result);
    beginDashboardSearch();
    setSearchReady(true);
  };

  const handleChangeFilter = (newFilter: DashboardFilterType) => {
    const result = { ...filter, ...newFilter };
    setFilter(result);
  };

  const handleImmediateApply = (newFilter: DashboardFilterType) => {
    const result = { ...filter, ...newFilter };
    setFilter(result);
    setAppliedFilter(result);
    beginDashboardSearch();
    setSearchReady(true);
  };

  const handleClickSearch = () => {
    beginDashboardSearch();
    setSearchReady(true);
    setAppliedFilter(filter);
  };

  const handleBidContractorChange = (ids: ValueType[]) => {
    setBidContractorIds(ids);
  };

  const handleSearchComplete = () => {
    if (searchReady) {
      setSearchReady(false);
    }
  };

  const onLoad = () => {};

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <ContentLayout>
      <DashboardTab onSearch={handleClickSearch} />
      <div className="flex gap-5">
        <DashboardFilter
          type="bid"
          filter={filter}
          onSetDefault={handleSetDefaultFilter}
          onChange={handleChangeFilter}
          onImmediateApply={handleImmediateApply}
          onBidContractorChange={handleBidContractorChange}
        />
        <div className="w-full flex flex-col gap-5">
          <DashboardBidSummary
            searchReady={searchReady}
            filter={appliedFilter}
            onSearchComplete={handleSearchComplete}
          />
          {/* First row - 2 columns */}
          <div className="grid grid-cols-2 gap-5">
            <DashboardStackedBarChart
              title="월별 공고건수"
              searchReady={searchReady}
              filter={appliedFilter}
              onSearchComplete={handleSearchComplete}
            />
            <DashboardPieChart
              type="bid"
              title="자사계약 품목구분별 비중"
              searchReady={searchReady}
              method={GetProductRatio}
              filter={appliedFilter}
              onSearchComplete={handleSearchComplete}
            />
          </div>
          {/* Second row - 2 columns */}
          <div className="grid grid-cols-2 gap-5">
            <DashboardHorizontalBarChart
              type="bid"
              title="계약수량 Top 10 품목"
              searchReady={searchReady}
              method={GetStatsByProducts}
              filter={appliedFilter}
              label="product_name"
              value="contract_count"
              onSearchComplete={handleSearchComplete}
            />
            <DashboardTTPComparisonChart
              title="TTP 제품 비교 차트"
              searchReady={searchReady}
              filter={appliedFilter}
              onSearchComplete={handleSearchComplete}
            />
          </div>
          {/* New row - 2 columns (수요기관 필터 1개 이상 선택 시에만 표시) */}
          {appliedFilter.dmin_codes.length > 0 && (
            <div className="grid grid-cols-2 gap-5">
              <DashboardDemandAgencyBudgetRatio
                title="수요기관 별 집행예산 비율"
                searchReady={searchReady}
                filter={appliedFilter}
                selectedAgencies={appliedFilter.dmin_codes}
                onSearchComplete={handleSearchComplete}
              />
              <DashboardDemandAgencySupplyEquipmentRatio
                title="수요기관 별 소모품 장비 비율"
                searchReady={searchReady}
                filter={appliedFilter}
                selectedAgencies={appliedFilter.dmin_codes}
                onSearchComplete={handleSearchComplete}
              />
            </div>
          )}
          {/* Third row - 3 columns */}
          <div className="grid grid-cols-3 gap-5">
            <DashboardSingleBarChart
              title="자사 월별 계약금액"
              searchReady={searchReady}
              filter={appliedFilter}
              onSearchComplete={handleSearchComplete}
            />
            <DashboardLineChart
              title="월별 자사 시약제품 비중"
              searchReady={searchReady}
              filter={appliedFilter}
              onSearchComplete={handleSearchComplete}
            />
            <DashboardTopTenList
              title="계약금액 Top 10 업체"
              searchReady={searchReady}
              filter={appliedFilter}
              onSearchComplete={handleSearchComplete}
            />
          </div>
          {/* Recent Bid Table */}
          <DashboardContractTable
            type="bid"
            searchReady={searchReady}
            filter={appliedFilter}
            onSearchComplete={handleSearchComplete}
            bidContractorIds={bidContractorIds}
          />
        </div>
      </div>
    </ContentLayout>
  );
}
